import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { AssistantRule, KnowledgeDocument, EmbeddingResponse } from '@/components/ai-assistant/management/types';
import { 
  truncateContext, 
  validateContextSize, 
  countTokens,
  calculateMessagesTokens,
  calculateKnowledgeTokens 
} from '@/services/ai-context-management-service';
import { expandQuery, combineExpandedResults } from '@/services/ai-query-expansion-service';

export function useAiManagement() {
  const [isGeneratingEmbedding, setIsGeneratingEmbedding] = useState(false);

  // Generate embeddings for text using the Supabase Edge Function
  const generateEmbedding = async (text: string): Promise<number[] | null> => {
    setIsGeneratingEmbedding(true);
    
    try {
      const { data, error } = await supabase.functions.invoke<EmbeddingResponse>('generate-embedding', {
        body: { text }
      });

      if (error) {
        throw new Error(`Error invoking edge function: ${error.message}`);
      }

      if (!data || !data.embedding) {
        throw new Error('No embedding returned from API');
      }

      return data.embedding;
    } catch (error) {
      console.error('Error generating embedding:', error);
      toast.error('Failed to generate embedding for text');
      return null;
    } finally {
      setIsGeneratingEmbedding(false);
    }
  };

  // Generate embeddings in batch with rate limiting
  const generateEmbeddingsBatch = async (
    texts: string[], 
    options: { 
      concurrency?: number,
      onProgress?: (completed: number, total: number) => void 
    } = {}
  ): Promise<(number[] | null)[]> => {
    const { concurrency = 3, onProgress } = options;
    const results: (number[] | null)[] = [];
    let completed = 0;

    // Process in batches for rate limiting
    for (let i = 0; i < texts.length; i += concurrency) {
      const batch = texts.slice(i, i + concurrency);
      
      // Process this batch in parallel
      const batchPromises = batch.map(text => generateEmbedding(text));
      const batchResults = await Promise.all(batchPromises);
      
      results.push(...batchResults);
      
      completed += batch.length;
      if (onProgress) {
        onProgress(completed, texts.length);
      }
      
      // Small delay to avoid rate limiting
      if (i + concurrency < texts.length) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }
    
    return results;
  };

  // Test if a regex pattern is valid and matches a test string
  const testRegexPattern = (pattern: string, testString: string): boolean => {
    try {
      const regex = new RegExp(pattern, 'i');
      return regex.test(testString);
    } catch (e) {
      return false;
    }
  };

  // Extract suggested questions from a response text
  const extractSuggestedQuestions = (responseText: string): string[] => {
    const suggestionsMatch = responseText.match(/SUGGESTED_QUESTIONS:\s*\n-\s*(.*?)(?:\n|$)\s*-\s*(.*?)(?:\n|$)\s*-\s*(.*?)(?:\n|$)/s);
    
    if (suggestionsMatch) {
      return suggestionsMatch
        .slice(1)
        .map(s => s.trim())
        .filter(s => s.length > 0);
    }
    
    return [];
  };

  // Convert single document to multi-language format
  const convertToMultiLanguage = async (
    docId: string, 
    language: string = 'en'
  ): Promise<boolean> => {
    try {
      const { data: doc, error: fetchError } = await supabase
        .from('assistant_knowledge')
        .select('metadata')
        .eq('id', docId)
        .single();
      
      if (fetchError) {
        throw fetchError;
      }
      
      const updatedMetadata = {
        ...(doc.metadata || {}),
        language: language,
        is_multi_language: true,
        source_language: language,
        supported_languages: ['en', 'vi', 'zh', 'th', 'ko', 'ja'],
        multi_language_updated_at: new Date().toISOString()
      };
      
      const { error: updateError } = await supabase
        .from('assistant_knowledge')
        .update({ metadata: updatedMetadata })
        .eq('id', docId);
        
      if (updateError) {
        throw updateError;
      }
      
      toast.success('Document converted to multi-language format');
      return true;
    } catch (error) {
      console.error('Error converting document:', error);
      toast.error('Failed to convert document');
      return false;
    }
  };

  // Enhanced helper to convert multiple documents to multi-language format
  const convertBulkToMultiLanguage = async (
    options: {
      language?: string,
      topic?: string,
      sourceLanguage?: string,
      supportedLanguages?: string[]
    } = {}
  ): Promise<{ success: number; failed: number }> => {
    const { language, topic, sourceLanguage = 'vi', supportedLanguages = ['en', 'vi', 'zh', 'th', 'ko', 'ja'] } = options;
    let query = supabase.from('assistant_knowledge').select('id, metadata');
    
    // Apply filters if provided
    if (language) {
      query = query.eq('metadata->language', language);
    }
    
    if (topic) {
      query = query.eq('metadata->topic', topic);
    }
    
    try {
      const { data, error } = await query;
      
      if (error) {
        throw error;
      }
      
      if (!data || data.length === 0) {
        toast.info('No documents found matching the criteria');
        return { success: 0, failed: 0 };
      }
      
      let successCount = 0;
      let failedCount = 0;
      
      // Process each document with enhanced metadata
      for (const doc of data) {
        try {
          // Update the document metadata with enhanced multi-language support
          const updatedMetadata = {
            ...(doc.metadata || {}),
            language: doc.metadata?.language || 'en',
            is_multi_language: true,
            source_language: sourceLanguage,
            supported_languages: supportedLanguages,
            multi_language_updated_at: new Date().toISOString()
          };
          
          const { error: updateError } = await supabase
            .from('assistant_knowledge')
            .update({ metadata: updatedMetadata })
            .eq('id', doc.id);
            
          if (updateError) {
            throw updateError;
          }
          
          successCount++;
        } catch (error) {
          console.error(`Error updating document ${doc.id}:`, error);
          failedCount++;
        }
      }
      
      toast.success(`Successfully converted ${successCount} documents, ${failedCount} failed`);
      return { success: successCount, failed: failedCount };
    } catch (error) {
      console.error('Error in bulk conversion:', error);
      toast.error('Failed to convert documents to multi-language format');
      return { success: 0, failed: 0 };
    }
  };

  // New enhanced functions for RAG improvements
  const testQueryExpansion = async (query: string, language: string = 'en') => {
    try {
      const expansion = await expandQuery(query, language);
      console.log('Query expansion test:', expansion);
      toast.success(`Generated ${expansion.expandedQueries.length} expanded queries`);
      return expansion;
    } catch (error) {
      console.error('Error testing query expansion:', error);
      toast.error('Failed to test query expansion');
      return null;
    }
  };

  const testContextManagement = (messages: any[], knowledgeResults: any[], model: string = 'gpt-4o-mini') => {
    try {
      const validation = validateContextSize(messages, knowledgeResults, model);
      const truncation = truncateContext(messages, knowledgeResults, model);
      
      console.log('Context management test:', { validation, truncation });
      
      if (validation.valid) {
        toast.success(`Context is valid (${validation.tokens}/${validation.limit} tokens)`);
      } else {
        toast.warning(`Context truncated: ${truncation.removedItems} items removed`);
      }
      
      return { validation, truncation };
    } catch (error) {
      console.error('Error testing context management:', error);
      toast.error('Failed to test context management');
      return null;
    }
  };

  // New function to test chunking functionality
  const testChunking = async (content: string, options?: any) => {
    try {
      const { chunkDocument } = await import('@/services/ai-content-chunking-service');
      
      const chunks = chunkDocument('test-doc', content, {
        maxChunkSize: 1000,
        overlapSize: 200,
        preserveStructure: true,
        language: 'en',
        ...options
      });
      
      console.log('Chunking test result:', {
        originalLength: content.length,
        chunkCount: chunks.length,
        chunks: chunks.map(c => ({
          id: c.id,
          length: c.content.length,
          index: c.chunkIndex
        }))
      });
      
      toast.success(`Content chunked into ${chunks.length} pieces successfully`);
      return chunks;
    } catch (error) {
      console.error('Error testing chunking:', error);
      toast.error('Failed to test chunking');
      return null;
    }
  };

  // Function to verify document processing status
  const checkDocumentProcessingStatus = async (documentId: string) => {
    try {
      const { data, error } = await supabase
        .from('assistant_knowledge')
        .select('metadata, document')
        .eq('id', documentId)
        .single();
        
      if (error) throw error;
      
      const isChunked = data.metadata?.document_type === 'chunked';
      const chunkCount = data.metadata?.chunk_count || 0;
      const originalLength = data.metadata?.original_length || 0;
      
      console.log('Document processing status:', {
        isChunked,
        chunkCount,
        originalLength,
        metadata: data.metadata
      });
      
      return {
        isChunked,
        chunkCount,
        originalLength,
        processingType: isChunked ? 'chunked' : 'single'
      };
    } catch (error) {
      console.error('Error checking document status:', error);
      return null;
    }
  };

  return {
    isGeneratingEmbedding,
    generateEmbedding,
    generateEmbeddingsBatch,
    testRegexPattern,
    extractSuggestedQuestions,
    convertToMultiLanguage,
    convertBulkToMultiLanguage,
    // Enhanced RAG functions
    testQueryExpansion,
    testContextManagement,
    testChunking, // New function
    checkDocumentProcessingStatus, // New function
    // Direct access to utility functions
    countTokens,
    calculateMessagesTokens,
    calculateKnowledgeTokens,
    truncateContext,
    validateContextSize,
    expandQuery,
    combineExpandedResults
  };
}
